import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

interface UseUrlFiltersOptions {
  defaultFilters?: Record<string, string>;
}

/**
 * Custom hook to manage filters synchronized with URL query parameters
 * This provides a reusable pattern for pages that need filtering with URL persistence
 */
export function useUrlFilters(options: UseUrlFiltersOptions = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { defaultFilters = {} } = options;

  // Initialize filters from URL params or defaults
  const getInitialFilters = useCallback(() => {
    const filters: Record<string, string> = { ...defaultFilters };
    searchParams.forEach((value, key) => {
      filters[key] = value;
    });
    return filters;
  }, [searchParams, defaultFilters]);

  const [filters, setFilters] = useState<Record<string, string>>(getInitialFilters);

  // Sync filters to URL
  const updateFilters = useCallback(
    (newFilters: Record<string, string | null>) => {
      setFilters((prev) => {
        const updated = { ...prev };
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === null || value === "") {
            delete updated[key];
          } else {
            updated[key] = value;
          }
        });
        return updated;
      });

      // Update URL
      const newParams = new URLSearchParams();
      Object.entries({ ...filters, ...newFilters }).forEach(([key, value]) => {
        if (value && value !== "") {
          newParams.set(key, value);
        }
      });
      setSearchParams(newParams, { replace: true });
    },
    [filters, setSearchParams],
  );

  const setFilter = useCallback(
    (key: string, value: string | null) => {
      updateFilters({ [key]: value });
    },
    [updateFilters],
  );

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setSearchParams({}, { replace: true });
  }, [defaultFilters, setSearchParams]);

  const getFilter = useCallback(
    (key: string, defaultValue: string = "") => {
      return filters[key] || defaultValue;
    },
    [filters],
  );

  return {
    filters,
    setFilter,
    updateFilters,
    clearFilters,
    getFilter,
  };
}
